import axios from 'axios';
import { globalURLS } from '../../Global/urls.js';

export interface MiniTag {
    id: string;
    name: string;
}

export interface MiniCreator {
    accountid: string;
    firstname: string;
    lastname: string;
    profilepic?: string;
}

export interface MiniDiscussion {
    discussionid: string;
    name: string;
    coverimage?: string;
    startdate?: string;
    enddate?: string;
}

export interface Mini {
    id: string;
    title: string;
    description?: string;
    url: string;
    durationinms: number;
    thumbnailurl?: string;
    transcript?: string;
    storyboard?: string;
    resizeMode?: string;
    isactive: number;
    createdon?: string;
    modifiedon?: string;
    tags: MiniTag[];
    creators: MiniCreator[];
    discussion: MiniDiscussion | null;
}

export interface TagOption {
    id: string;
    name: string;
    miniCount?: number;
}

export interface DiscussionOption {
    discussionid: string;
    name: string;
    description?: string;
    coverimage?: string;
    startdate?: string;
    enddate?: string;
    isactive?: number;
}

export interface UpsertMiniPayload {
    id?: string;
    title: string;
    url: string;
    durationinms?: number;
    thumbnailurl?: string;
    transcript?: string;
    resize_mode?: string;
    discussionid?: string | null;
    creators?: { accountid: string }[];
    tags?: ({ id: string } | { name: string })[];
    isactive?: number;
}

const post = async <T>(endpoint: string, body: Record<string, unknown> = {}): Promise<T> => {
    const response = await axios.post(
        `${globalURLS.baseURL}minis/captain/${endpoint}`,
        body,
        globalURLS.config,
    );
    if (!response.data?.success) {
        throw new Error(response.data?.message || 'Request failed');
    }
    return response.data.data as T;
};

const postArray = async <T>(endpoint: string, body: Record<string, unknown> = {}): Promise<T[]> => {
    const data = await post<unknown>(endpoint, body);
    return Array.isArray(data) ? (data as T[]) : [];
};

export const getAllMinis = (params: {
    count?: number;
    offset?: number;
    searchquery?: string;
    includeInactive?: boolean;
}) =>
    postArray<Mini>('get_all_minis', {
        count: params.count ?? 50,
        offset: params.offset ?? 0,
        ...(params.searchquery?.trim() ? { searchquery: params.searchquery.trim() } : {}),
        includeInactive: params.includeInactive ?? false,
    });

export const getAllTags = (searchquery?: string) =>
    postArray<TagOption>('get_all_tags', searchquery?.trim() ? { searchquery: searchquery.trim() } : {});

export const getAllDiscussions = (params: {
    count?: number;
    offset?: number;
    searchquery?: string;
    includeInactive?: boolean;
}) =>
    postArray<DiscussionOption>('get_all_discussions', {
        count: params.count ?? 50,
        offset: params.offset ?? 0,
        includeInactive: params.includeInactive ?? false,
        ...(params.searchquery?.trim() ? { searchquery: params.searchquery.trim() } : {}),
    });

export const upsertMini = (payload: UpsertMiniPayload) => post<Mini>('upsert_mini', payload);

export const deleteMini = (id: string) => post<{ id: string }>('delete_mini', { id });

export interface MinisBannerActive {
    id: string;
    imageUrl: string;
    rank: number;
    action: string;
    openWebviewUrl: string;
    openDeeplink: string;
    videoUrl?: string;
    duration?: number;
    startdate?: string;
    enddate?: string;
}

export interface MinisBanner extends MinisBannerActive {
    startdate: string;
    enddate: string;
    isactive: number;
}

export interface UpdateMinisBannerPayload {
    id?: string;
    imageUrl: string;
    rank?: number;
    startdate: string;
    enddate: string;
    action?: string;
    openWebviewUrl?: string;
    openDeeplink?: string | Record<string, unknown>;
    isactive?: number;
}

export const getActiveMinisBanners = () =>
    postArray<MinisBannerActive>('get_active_minis_banners');

export const updateMinisBanners = (banners: UpdateMinisBannerPayload[]) =>
    postArray<MinisBanner>('update_minis_banners', { banners });
